import { USBGrid } from '@usb-shield/react-grid'
import '@usb-shield/react-grid/dist/library/styles/index.css'
import { defaultGridOptions } from '@/utils/usb-grid.util'
import pageHeaderStyles from '@/components/PageHeader/styles/pageHeader.module.scss'
import { MobileNavBar } from '@/components/PageHeader/MobileNavBar'
import Head from 'next/head'
import { fetchProductVersions } from '@/utils/products/fetch-product-version-and-user-token'
import { useProductListQuery } from '@/modules/products/services/product-list.query'
import ChallengeWidgetModal from '@/modules/challenge-widget/ChallengeWidget'
import { useEffect, useState, useRef } from 'react'
import { HeaderTierTop } from '@/components/PageHeader/components/HeaderTierTop'
import { HeaderTierBottom } from '@/components/PageHeader/components/HeaderTierBottom'
import { toggleLevel2Callback, escapeKeyPressed, togglebackOneLevelCallback } from '@/components/PageHeader/MobileNavBarUseEffectUtils'
import { toggleOverlayAndMenuVisibility } from '@/components/PageHeader/components/utils/ToggleOverlayAndMenuVisibility'
import { ChatBotModal } from '@/components/ChatBotModal/ChatBotModal'

const headerDefaultGridOptions = {
  ...defaultGridOptions,
  padding: 'zero',
}

export const PageHeader = (headerData: any): JSX.Element => {
  const [activityName, setActivityName] = useState<string>('')
  const [callBackUrl, setCallBackUrl] = useState<string>('')
  const regionOptions = headerData?.regionOptions
  const [profileMenuArrowFlag, setProfileMenuArrowFlag] = useState(false)
  const listMainMenu = useRef<HTMLUListElement>(null)
  const loginButtonRef = useRef<HTMLButtonElement>(null)
  const menuWrapperRef = useRef<HTMLDivElement>(null)
  const toggleMenuCallback = () => {
    // Define the toggle menu callback logic here
  }

  const type = headerData?.type
  const menuItems = headerData?.menuItems
  const imageURL = headerData?.imageURL
  const mainmenuId = headerData?.mainmenuId
  const fullWidthLayout = headerData?.fullWidthLayout || false
  const pageTitle = headerData?.title ? headerData.title : false
  const { useProductVersionsListQuery } = useProductListQuery()
  const { data: productListData } = useProductVersionsListQuery()
  let allProductVersionsInfo
  const [challengeModalIsOpen, setChallengeModalIsOpen] = useState<boolean>(false)
  const [isChatBotModalOpen, setIsChatBotModalOpen] = useState(false)

  /**
   * Opens the ChatBotModal.
   */
  function onDeveloperAssistantClick(): void {
    setIsChatBotModalOpen(true)
  }

  /**
   * Closes the ChatBotModal.
   */
  function handleCloseChatBotModal(): void {
    setIsChatBotModalOpen(false)
  }

  useEffect(() => {}, [challengeModalIsOpen, callBackUrl])

  useEffect(() => {
    document.addEventListener('keydown', (event: any) => escapeKeyPressed(event, menuWrapperRef, toggleMenuCallback), false)
    return () => {
      document.removeEventListener('keydown', (event: any) => escapeKeyPressed(event, menuWrapperRef, toggleMenuCallback), false)
    }
  }, [])

  allProductVersionsInfo = fetchProductVersions(productListData)

  if (process.env.HEADER_TIER_BOTTOM_ENABLED === undefined) {
    console.error('undefined header environment variable')
  }

  return (
    <>
      <Head>
      {pageTitle &&
        <title>{pageTitle}</title>
      }
        <meta
          name="description"
          key="description"
          content="The API Dev Portal provides comprehensive documentation for use by API developers."
          />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      {process.env.HEADER_TIER_BOTTOM_ENABLED === 'true' && <hr className={pageHeaderStyles.twoTierMenuSeperator}></hr>}
      <div id="header-container" className={process.env.SITE_BRAND.toLowerCase() === 'elavon' ? pageHeaderStyles.desktopNavBar: pageHeaderStyles.twoTierNavBar}>
        <section className={pageHeaderStyles.sectionRow}>
          <USBGrid
            alignItems={defaultGridOptions.alignItems}
            columnCount={defaultGridOptions.columnCount}
            justifyContent={defaultGridOptions.justifyContent}
            display={defaultGridOptions.display}
            padding={process.env.SITE_BRAND.toLowerCase() === 'elavon' ? defaultGridOptions.padding : 'zero'}
            addClasses={(fullWidthLayout ? pageHeaderStyles.gridWrapperFullWidth : pageHeaderStyles.gridWrapper) + ' wrapperFullWidth'}
          >
            {/* render MFA Challenge widget modal */}
            {challengeModalIsOpen && (
              <ChallengeWidgetModal
                callBackUrl={callBackUrl}
                challengeModalIsOpen={challengeModalIsOpen}
                setChallengeModalIsOpen={setChallengeModalIsOpen}
                authWidgetConfigData={headerData.authWidgetConfigData}
                activityName={activityName}
              ></ChallengeWidgetModal>
            )}
            <div
              className="showOverlay"
              data-testid="hideOverlayAndMenu"
              onClick={(event: any) => toggleOverlayAndMenuVisibility(profileMenuArrowFlag, setProfileMenuArrowFlag, listMainMenu, loginButtonRef)}
            ></div>
            {/* Desktop Menu */}
            <HeaderTierTop
              type={type}
              menuItems={process.env.SITE_BRAND.toLowerCase() === 'elavon' ? menuItems : null}
              imageURL={imageURL}
              mainmenuId={mainmenuId}
              fullWidthLayout={false}
              regionOptions={regionOptions}
              isLoginSignUpDisabled={headerData.isLoginSignUpDisabled}
              isSearchDisabled={headerData.isSearchDisabled}
              allProductVersionsInfo={allProductVersionsInfo}
              authWidgetConfigData={headerData.authWidgetConfigData}
              onDeveloperAssistantClick={onDeveloperAssistantClick}
            ></HeaderTierTop>
            {process.env.HEADER_TIER_BOTTOM_ENABLED === 'true' && (
              <HeaderTierBottom
                  menuItems={menuItems}
                  mainmenuId={mainmenuId}
                  fullWidthLayout={fullWidthLayout}
                  authWidgetConfigData={headerData.authWidgetConfigData}
                  allProductVersionsInfo={allProductVersionsInfo}
              ></HeaderTierBottom>
            )}
          </USBGrid>
        </section>
      </div>
      <div className={process.env.SITE_BRAND.toLowerCase() === 'elavon' ? pageHeaderStyles.mobileNavBar : pageHeaderStyles.twoTierMobileNavBar}>
        <section className={pageHeaderStyles.sectionRow}>
          <USBGrid
            gridGap={defaultGridOptions.gridGap}
            alignItems={defaultGridOptions.alignItems}
            columnCount={defaultGridOptions.columnCount}
            justifyContent={defaultGridOptions.justifyContent}
            display={defaultGridOptions.display}
            padding={headerDefaultGridOptions.padding}
          >
            {/* Mobile Menu */}
            <MobileNavBar
              type={type}
              menuItems={menuItems}
              imageURL={imageURL}
              mainmenuId={mainmenuId}
              regionOptions={regionOptions}
              isLoginSignUpDisabled={headerData.isLoginSignUpDisabled}
              isSearchDisabled={headerData.isSearchDisabled}
              allProductVersionsInfo={allProductVersionsInfo}
              authWidgetConfigData={headerData.authWidgetConfigData}
              toggleLevel2Callback={toggleLevel2Callback}
              togglebackOneLevelCallback={togglebackOneLevelCallback}
            ></MobileNavBar>
          </USBGrid>
        </section>
      </div>
      <ChatBotModal isOpen={isChatBotModalOpen} onClose={handleCloseChatBotModal} />
    </>
  )
}